import { Alert, Button, Col, Row, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";

import {
  selectCampaignForUpdate,
  selectIsUpdateCampaign,
} from "../../../../redux";
import { DataCampaigns } from "../../../../redux/@types";
import {
  InputSelect,
  InputText,
} from "../../../../shared/components";
import Upload from "../../../../shared/components/ui/inputs/Upload";
import { PATH_S3_BUCKET } from "../../../../shared/utils/constants";
import { productCategoriesService } from "../../../../shared/services";
import { useCampaigns } from "../../hooks";
import { CampaignColorField } from "./CampaignColorField";

const { Title, Text } = Typography;

function slugifyPreview(name?: string | null) {
  if (!name) return "";
  return name
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export const CampaignsForm = () => {
  const isUpdateCampaign = useSelector(selectIsUpdateCampaign);
  const updateCampaign = useSelector(selectCampaignForUpdate);
  const { isLoading, onSubmitCreateOrUpdate } = useCampaigns();
  const [categoryOptions, setCategoryOptions] = useState<
    { title: string; value: number }[]
  >([]);

  const {
    control,
    handleSubmit: onSubmit,
    setValue,
    watch,
  } = useFormContext<DataCampaigns>();

  const name = watch("name");
  const status = watch("status");
  const productsMode = watch("products_mode");
  const heroImage = watch("hero_image");
  const heroImageMobile = watch("hero_image_mobile");
  const secondaryImage = watch("secondary_image");
  const secondaryImageMobile = watch("secondary_image_mobile");
  const pillBg = watch("pill_bg_color") || "#FE4914";
  const pillText = watch("pill_text_color") || "#FFFFFF";
  const pillLabel = watch("pill_label") || name || "Campaña";

  const previewSrc = (path?: string | null) => {
    if (!path) return null;
    if (
      path.startsWith("/") ||
      path.startsWith("http://") ||
      path.startsWith("https://")
    ) {
      return path;
    }
    return `${PATH_S3_BUCKET}/${path}`;
  };

  const slugPreview = useMemo(
    () => updateCampaign?.slug || slugifyPreview(name),
    [updateCampaign?.slug, name]
  );

  useEffect(() => {
    productCategoriesService
      .find({
        query: {
          $limit: 200,
          status: "active",
          $sort: { name: 1 },
        },
      })
      .then((res: any) => {
        const rows = Array.isArray(res) ? res : res?.data || [];
        setCategoryOptions(
          rows.map((item: { id: number; name: string }) => ({
            title: item.name,
            value: item.id,
          }))
        );
      })
      .catch(() => setCategoryOptions([]));
  }, []);

  return (
    <Row justify="start" gutter={[8, 8]} style={{ width: "100%", padding: 10 }}>
      <Col span={24}>
        <Title
          style={{ marginBottom: "1.5px", color: "var(--primary)" }}
          level={2}
        >
          {isUpdateCampaign
            ? `Actualizando campaña: ${updateCampaign?.id}`
            : `Creando campaña`}
        </Title>
      </Col>

      {status === "active" ? (
        <Col span={24}>
          <Alert
            type="warning"
            showIcon
            message="Solo puede haber una campaña activa. Al publicar esta, se desactivará cualquier otra campaña activa y este será el botón del header."
          />
        </Col>
      ) : null}

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Nombre:
        </Title>
        <InputText
          placeHolder="Día de las Madres"
          name="name"
          control={control}
        />
        {slugPreview ? (
          <Text type="secondary" style={{ marginTop: 6 }}>
            URL pública: /{slugPreview}
          </Text>
        ) : null}
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Estado:
        </Title>
        <InputSelect
          name="status"
          control={control}
          dataOptions={[
            { title: "Activo (publicada)", value: "active" },
            { title: "Inactivo", value: "inactive" },
          ]}
          label="Estado"
        />
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Modo de productos:
        </Title>
        <InputSelect
          name="products_mode"
          control={control}
          dataOptions={[
            { title: "Manual", value: "manual" },
            { title: "Categoría", value: "category" },
            { title: "Promociones / ofertas", value: "promotions" },
          ]}
          label="Modo"
        />
      </Col>

      {productsMode === "category" ? (
        <Col
          span={12}
          style={{
            display: "flex",
            marginTop: "10px",
            padding: 5,
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Title style={{ marginBottom: "1.5px" }} level={5}>
            Categoría:
          </Title>
          <InputSelect
            name="category_id"
            control={control}
            dataOptions={categoryOptions}
            label="Categoría"
          />
        </Col>
      ) : null}

      {productsMode === "promotions" ? (
        <Col span={24} style={{ marginTop: 8 }}>
          <Alert
            type="info"
            showIcon
            message="En la web se cargarán automáticamente los productos con descuento/oferta."
          />
        </Col>
      ) : null}

      <Col span={24} style={{ marginTop: 8 }}>
        <Alert
          type="info"
          showIcon
          message="El banner principal (hero) es solo imagen: los textos y botones deben ir en el arte."
        />
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Título selección:
        </Title>
        <InputText
          placeHolder="Selección Curada"
          name="featured_title"
          control={control}
        />
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Descripción selección:
        </Title>
        <InputText
          placeHolder="Hemos seleccionado lo mejor..."
          name="featured_description"
          control={control}
          rows
        />
      </Col>

      <Col span={24} style={{ marginTop: 16 }}>
        <Title level={4} style={{ color: "var(--primary)" }}>
          Botón del header
        </Title>
      </Col>

      <Col
        span={8}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Texto del botón:
        </Title>
        <InputText
          placeHolder="Día de las Madres"
          name="pill_label"
          control={control}
        />
      </Col>

      <Col
        span={8}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <CampaignColorField
          name="pill_bg_color"
          control={control}
          label="Color fondo:"
          defaultValue="#FE4914"
        />
      </Col>

      <Col
        span={8}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <CampaignColorField
          name="pill_text_color"
          control={control}
          label="Color texto:"
          defaultValue="#FFFFFF"
        />
      </Col>

      <Col span={24} style={{ marginTop: 8, padding: 5 }}>
        <span
          style={{
            display: "inline-block",
            borderRadius: 999,
            padding: "8px 24px",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            backgroundColor: pillBg,
            color: pillText,
          }}
        >
          {pillLabel}
        </span>
      </Col>

      <Col span={24} style={{ marginTop: 16 }}>
        <Title level={4} style={{ color: "var(--primary)" }}>
          Banner principal (hero)
        </Title>
        <Text type="secondary">
          Solo imagen. Sube versión desktop y mobile. Desktop: 2560 × 1360 px.
          Mobile: 1536 × 1200 px.
        </Text>
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Hero desktop:
        </Title>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 6 }}>
          Recomendado: 2560 × 1360 px
        </Text>
        {previewSrc(heroImage) ? (
          <img
            src={previewSrc(heroImage)!}
            alt="hero desktop"
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "250px",
              maxHeight: "250px",
              objectFit: "cover",
            }}
          />
        ) : null}
        <Upload
          filePath="campaigns"
          onUploadedFile={(files) => {
            const file = files[0];
            setValue("hero_image", file.fileKey);
            message.success("Hero desktop actualizado");
          }}
        />
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Hero mobile:
        </Title>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 6 }}>
          Recomendado: 1536 × 1200 px
        </Text>
        {previewSrc(heroImageMobile) ? (
          <img
            src={previewSrc(heroImageMobile)!}
            alt="hero mobile"
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "250px",
              maxHeight: "250px",
              objectFit: "cover",
            }}
          />
        ) : null}
        <Upload
          filePath="campaigns"
          onUploadedFile={(files) => {
            const file = files[0];
            setValue("hero_image_mobile", file.fileKey);
            message.success("Hero mobile actualizado");
          }}
        />
      </Col>

      <Col span={24} style={{ marginTop: 16 }}>
        <Title level={4} style={{ color: "var(--primary)" }}>
          Banner inferior
        </Title>
        <Text type="secondary">
          Solo imagen. Sube versión desktop y mobile. Desktop: 2400 × 800 px.
          Mobile: 1536 × 768 px.
        </Text>
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Inferior desktop:
        </Title>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 6 }}>
          Recomendado: 2400 × 800 px
        </Text>
        {previewSrc(secondaryImage) ? (
          <img
            src={previewSrc(secondaryImage)!}
            alt="banner inferior desktop"
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "250px",
              maxHeight: "250px",
              objectFit: "cover",
            }}
          />
        ) : null}
        <Upload
          filePath="campaigns"
          onUploadedFile={(files) => {
            const file = files[0];
            setValue("secondary_image", file.fileKey);
            message.success("Banner inferior desktop actualizado");
          }}
        />
      </Col>

      <Col
        span={12}
        style={{
          display: "flex",
          marginTop: "10px",
          padding: 5,
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Title style={{ marginBottom: "1.5px" }} level={5}>
          Inferior mobile:
        </Title>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 6 }}>
          Recomendado: 1536 × 768 px
        </Text>
        {previewSrc(secondaryImageMobile) ? (
          <img
            src={previewSrc(secondaryImageMobile)!}
            alt="banner inferior mobile"
            style={{
              marginBottom: "7px",
              width: "100%",
              maxWidth: "250px",
              maxHeight: "250px",
              objectFit: "cover",
            }}
          />
        ) : null}
        <Upload
          filePath="campaigns"
          onUploadedFile={(files) => {
            const file = files[0];
            setValue("secondary_image_mobile", file.fileKey);
            message.success("Banner inferior mobile actualizado");
          }}
        />
      </Col>

      <Col span={24} style={{ marginTop: 16 }}>
        <Button
          type="primary"
          shape="round"
          size="large"
          loading={isLoading}
          onClick={onSubmit(onSubmitCreateOrUpdate)}
        >
          {isUpdateCampaign ? "Actualizar información" : "Crear campaña"}
        </Button>
      </Col>
    </Row>
  );
};
